import { ISelectItem } from "@components/inputs/select/Select.types";
import { SwitchType } from "@components/inputs/switch/Switch";
import { getSimpleBoolSelectItems, ifAll, ifAny, IGetValueArgs, IValidator, not } from "@components/smart/FieldInfo";
import {
    AttachmentsAdditionalProperties,
    AttachmentsDef,
    BirthNumberFieldDef,
    getAddressFields,
    SingleBusinessPartnerDef,
    withDisplayName
} from "@components/smart/GeneralFieldDefinition";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IFormGroupDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { TSmartODataTableStorage } from "@components/smart/smartTable/SmartODataTableBase";
import { TCellValue } from "@components/table";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    AddressEntity,
    EntitySetName,
    EntityTypeName,
    IPrTaxpayerDeclarationEntity,
    PrAttendanceEntity,
    PrOtherProviderAddressEntity,
    PrOtherProviderEmployerAddressEntity,
    PrTaxpayerDeclarationChildEntity,
    PrTaxpayerDeclarationDiscountEntity,
    PrTaxpayerDeclarationEntity,
    PrTaxpayerDeclarationOtherProviderEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrTaxDiscountTypeCode,
    PrTaxpayerDeclarationStatusCode,
    PrTaxpayerDeclarationTypeCode
} from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import {
    getNumberOursSummaryDef,
    getNumberRangeFieldDefs,
    NumberRangeAdditionalProperties
} from "@pages/numberRange/NumberRange.utils";
import {
    getEmployeeFilterDef,
    getEmployeeSelectDef,
    getEmployeeSummaryItemDef,
    getEmployeeTableColumnDefs
} from "@pages/payroll/employee/Employee.utils";
import {
    disabilityDiscountType,
    getPathsForDeductionType,
    getTogglePropPath,
    hasDisabilityDiscount,
    isThereOtherProviderPath,
    showChildrenPath,
} from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration.utils";
import TaxpayerDeclarationFormView, { ITaxPayerDeclarationCustomData } from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclarationFormView";
import { isNewEntity } from "@utils/definition.utils";
import { arrayInsert } from "@utils/general";
import { logger } from "@utils/log";
import i18next from "i18next";
import React from "react";
import { DefaultTheme } from "styled-components";

import { DASH_CHARACTER } from "../../../constants";
import {
    BasicInputSizes,
    FastEntryInputSizes,
    FieldType,
    NavigationSource,
    Sort,
    TextAlign,
    ValidatorType
} from "../../../enums";
import { ColoredText } from "../../../global.style";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import DateType, { DATE_MAX, DATE_MIN, DateFormat, getUtcDate, getUtcDayjs } from "../../../types/Date";
import { IFormDef } from "../../../views/formView/Form";
import { TableButtonsAction } from "../../../views/table/TableToolbar.utils";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition, TFieldsDefinition } from "../../PageUtils";

const switchDef = {
    type: FieldType.Switch,
    fieldSettings: {
        type: SwitchType.YesNo,
    },
    customizationData: {
        useForCustomization: false
    }
};

const isVisibleForDiscountType = (type: PrTaxDiscountTypeCode) => (args: IGetValueArgs) => {
    const discountType = (args.storage.getCustomData() as ITaxPayerDeclarationCustomData).discountTypes?.[type];
    const year = args.storage.data.entity.Year;
    if (discountType && year) {
        return getUtcDayjs().set("year", year).isBetween(discountType.DateValidFrom ?? DATE_MIN, discountType.DateValidTo ?? DATE_MAX, "year", "[]");
    }
    return true;
};

export const getGroupForDeductionType = (type: PrTaxDiscountTypeCode): IFormGroupDef => {
    return {
        id: type,
        isVisible: isVisibleForDiscountType(type),
        title: i18next.t(`TaxpayerDeclaration:Types.${type}`),
        togglePropPath: getTogglePropPath(type),
        isSmallBottomMargin: true,
        customizationData: {
            isLocked: true
        },
        rows: []
    };
};

const getFieldDefinitions = () => {
    return Object.values(PrTaxDiscountTypeCode).reduce((defs: TFieldsDefinition, type) => {
        const { switchPath } = getPathsForDeductionType(type);

        defs[switchPath] = {
            ...switchDef
        };
        return defs;
    }, {});
};

export const getThemeColorForDeclarationStatus = (status: PrTaxpayerDeclarationStatusCode): keyof DefaultTheme => {
    switch (status) {
        case PrTaxpayerDeclarationStatusCode.Signed:
            return "C_SEM_text_good";
        case PrTaxpayerDeclarationStatusCode.Draft:
            return "C_SEM_text_bad";
        case PrTaxpayerDeclarationStatusCode.WaitingForSignature:
            return "C_SEM_text_warning";
        case PrTaxpayerDeclarationStatusCode.N_A:
        case PrTaxpayerDeclarationStatusCode.Ended:
            return "C_TEXT_primary";
    }
};

const isEmployed = ({ storage, bindingContext }: IGetValueArgs): boolean => {
    let otherProviderBc = bindingContext.getParent();
    while (!otherProviderBc.isValidNavigation(PrTaxpayerDeclarationOtherProviderEntity.IsEmployed)) {
        otherProviderBc = otherProviderBc.getParent();
        if (otherProviderBc.isRoot()) {
            logger.error("isEmployed: Cannot find PrTaxpayerDeclarationOtherProviderEntity binding context");
            return false;
        }
    }
    return storage.getValue(otherProviderBc.navigate(PrTaxpayerDeclarationOtherProviderEntity.IsEmployed));
};

const isThereOtherProvider = (args: IGetValueArgs): boolean => {
    return args.storage.getValueByPath(isThereOtherProviderPath);
};

function isSigned(args: IGetValueArgs): boolean {
    return args.storage.data.entity.Status?.Code === PrTaxpayerDeclarationStatusCode.Signed;
}

function isEnded(args: IGetValueArgs): boolean {
    return args.storage.data.entity.Status?.Code === PrTaxpayerDeclarationStatusCode.Ended;
}

export const TempDateValidToPath = BindingContext.localContext("TempDateValidToPath");

export enum TaxPayerDeclarationGroupIds {
    Validity = "validity",
    Employee = "employee"
}

const dateApplyValidator: IValidator = {
    type: ValidatorType.Date,
    settings: {
        customValidator: (val: TValue): boolean => {
            const currentYear = getUtcDayjs().get("year");
            const inputYear = getUtcDayjs(val as Date).get("year");
            return (inputYear === currentYear || inputYear === currentYear - 1);
        },
        message: () => {
            // callback to ensure translation is loaded
            return i18next.t("TaxpayerDeclaration:Form.DateApplyValidation").toString();
        },
    }
};

const dateApplyFormatter = (isFilter: boolean) => (val: TValue): string => {
    if (!val) {
        return isFilter ? i18next.t("Common:General.Empty") : null;
    }
    return DateType.format(val as Date, DateFormat.monthAndYear);
};

interface ITaxPayerDefArgs {
    isSingleEmployeeView?: boolean;
}

export const getDefinitions: IGetDefinition = (_context, args?: ITaxPayerDefArgs): IDefinition => {
    const isSingleEmployeeView = args?.isSingleEmployeeView ?? false;

    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrTaxpayerDeclaration}Table`,
        filter: (args: IGetValueArgs) => {
            const filters: string[] = [`${PrTaxpayerDeclarationEntity.TypeCode} eq '${PrTaxpayerDeclarationTypeCode.Regular}'`];
            if (isSingleEmployeeView) {
                const employeeId = (args.storage as TSmartODataTableStorage).getCustomData().rootStorage.data.bindingContext.getKey();
                filters.push(`${PrTaxpayerDeclarationEntity.Employee}/Id eq ${employeeId}`);
            }
            return filters.join(" AND ");
        },
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            isValueHelp: true,
            defaultFilters: [
                PrTaxpayerDeclarationEntity.NumberOurs,
                PrTaxpayerDeclarationEntity.Status,
                PrTaxpayerDeclarationEntity.DateApplyFrom,
                PrTaxpayerDeclarationEntity.DateApplyTo,
            ],
            filterDefinition: {
                [PrTaxpayerDeclarationEntity.NumberOurs]: {
                    label: i18next.t("TaxpayerDeclaration:Summary.NumberOursLabel")
                },
                ...withDisplayName(PrTaxpayerDeclarationEntity.Status),
                [PrTaxpayerDeclarationEntity.DateSigned]: {},
                [PrTaxpayerDeclarationEntity.DateEnded]: {},
                [PrTaxpayerDeclarationEntity.DateApplyFrom]: {
                    type: FieldType.MonthYear
                },
                [PrTaxpayerDeclarationEntity.DateApplyTo]: {
                    type: FieldType.MonthYear
                }
            }
        }],
        columns: [
            PrTaxpayerDeclarationEntity.NumberOurs,
            PrTaxpayerDeclarationEntity.Status,
            PrTaxpayerDeclarationEntity.DateApplyFrom,
            PrTaxpayerDeclarationEntity.DateApplyTo
        ],
        columnDefinition: {
            [PrTaxpayerDeclarationEntity.NumberOurs]: {
                label: i18next.t("TaxpayerDeclaration:Summary.NumberOursLabel"),
            },
            [PrTaxpayerDeclarationEntity.Status]: {
                textAlign: TextAlign.Center,
                formatter: (val: TValue, args: IFormatOptions): TCellValue => {
                    const entity = args.entity as IPrTaxpayerDeclarationEntity;
                    return {
                        value: (
                            <ColoredText
                                color={getThemeColorForDeclarationStatus(entity.Status?.Code as PrTaxpayerDeclarationStatusCode)}>
                                {entity.Status?.Name}
                            </ColoredText>
                        ),
                        tooltip: entity.Status?.Name
                    };
                }
            },
            [PrTaxpayerDeclarationEntity.DateSigned]: {},
            [PrTaxpayerDeclarationEntity.DateEnded]: {},
            [PrTaxpayerDeclarationEntity.DateApplyFrom]: {
                formatter: dateApplyFormatter(false)
            },
            [PrTaxpayerDeclarationEntity.DateApplyTo]: {
                formatter: dateApplyFormatter(false)
            },
        },
        preventStoreVariant: isSingleEmployeeView,
        title: i18next.t("TaxpayerDeclaration:Title"),
        initialSortBy: [{ id: PrTaxpayerDeclarationEntity.NumberOurs, sort: Sort.Asc }],
        tabs: [],
        tabsSettings: {
            customFilter: (selectedTab: string) => {
                if (!selectedTab) {
                    return "";
                }
                return `${PrTaxpayerDeclarationEntity.Year} eq ${selectedTab} AND ${PrAttendanceEntity.Month}`;
            },
            withoutCounts: true
        }
    };

    // edit def for table view on employee page
    if (isSingleEmployeeView) {
        table.columns = arrayInsert(table.columns, PrTaxpayerDeclarationEntity.Year, 0);
        table.columnDefinition[PrTaxpayerDeclarationEntity.Year] = {
            formatter: val => val?.toString()
        };
    } else {
        Object.entries(getEmployeeTableColumnDefs(PrTaxpayerDeclarationEntity.Employee)).reverse().forEach((item) => {
            const [path, def] = item;
            table.columns = arrayInsert(table.columns, path, 1);
            table.columnDefinition[path] = def;
        });

        const [filterPath, filterDef] = Object.entries(getEmployeeFilterDef(PrTaxpayerDeclarationEntity.Employee))[0];
        table.filterBarDef[0].defaultFilters.push(filterPath);
        table.filterBarDef[0].filterDefinition[filterPath] = filterDef;
    }

    const summary: ISummaryItem[] = [
        getNumberOursSummaryDef("TaxpayerDeclaration"),
        getEmployeeSummaryItemDef(PrTaxpayerDeclarationEntity.Employee),
        {
            id: PrTaxpayerDeclarationEntity.Status,
            label: i18next.t("TaxpayerDeclaration:Form.Status"),
            formatter: (val: TValue, args: IFormatOptions) => {
                const entity = args.entity as IPrTaxpayerDeclarationEntity;
                return <ColoredText
                        color={getThemeColorForDeclarationStatus(entity.Status?.Code as PrTaxpayerDeclarationStatusCode)}>
                    {entity.Status?.Name}
                </ColoredText>;
            },
            isVisible: not(isNewEntity)
        },
        {
            id: PrTaxpayerDeclarationEntity.DateSigned,
            formatter: (val: TValue) => {
                return val ? DateType.localFormat(val as Date) : DASH_CHARACTER;
            },
            type: FieldType.Date,
            label: i18next.t("TaxpayerDeclaration:Form.DateSigned"),
            isVisible: ifAny(isSigned, isEnded)
        },
        {
            id: PrTaxpayerDeclarationEntity.DateEnded,
            formatter: (val: TValue) => {
                return val ? DateType.localFormat(val as Date) : DASH_CHARACTER;
            },
            label: i18next.t("TaxpayerDeclaration:Form.DateInvalidated"),
            isVisible: isEnded
        }
    ];

    const form: IFormDef = {
        id: `${EntityTypeName.PrTaxpayerDeclaration}Form`,
        translationFiles: getDefinitions.translationFiles,
        formControl: TaxpayerDeclarationFormView,
        files: {
            ...AttachmentsDef,
            enabledInReadOnlyMode: true
        },
        isDeletable: true,
        isReadOnly: args => [PrTaxpayerDeclarationStatusCode.Signed, PrTaxpayerDeclarationStatusCode.Ended].includes(args.storage.data.entity.Status?.Code),
        summary,

        getItemBreadCrumbText: (storage: Model) =>
            getItemBreadCrumbsText(storage, i18next.t("TaxpayerDeclaration:FormTitle"), storage.data.entity?.NumberOurs),
        customHeaderActions: [{
            id: TableButtonsAction.MassExport,
            label: i18next.t("TaxpayerDeclaration:MassExportPdf.TitleSingle"),
            iconName: "ExportPdf",
            ignoreMasterDisabled: true,
            isVisible: not(isNewEntity)
        }],
        title: i18next.t("TaxpayerDeclaration:FormTitle"),
        additionalProperties: [
            ...AttachmentsAdditionalProperties,
            {
                id: PrTaxpayerDeclarationEntity.Children
            }, {
                id: PrTaxpayerDeclarationEntity.Discounts,
                additionalProperties: [{
                    id: PrTaxpayerDeclarationDiscountEntity.DiscountType
            }]

        }, ...NumberRangeAdditionalProperties],
        fieldDefinition: {
            ...getNumberRangeFieldDefs("TaxpayerDeclaration"),
            ...getFieldDefinitions(),
            [showChildrenPath]: {
                ...switchDef
            },
            [hasDisabilityDiscount]: {
                ...switchDef
            },
            [PrTaxpayerDeclarationEntity.Employee]: {
                ...getEmployeeSelectDef(),
                isVisible: ifAll(isNewEntity, !isSingleEmployeeView)
            },
            [PrTaxpayerDeclarationEntity.DateApplyFrom]: {
                type: FieldType.MonthYear,
                defaultValue: getUtcDayjs().startOf("year").toDate(),
                validator: dateApplyValidator
            },
            [PrTaxpayerDeclarationEntity.DateApplyTo]: {
                type: FieldType.MonthYear,
                clearIfInvisible: false,
                isVisible: isEnded,
                validator: dateApplyValidator
            },
            [TempDateValidToPath]: {
                type: FieldType.MonthYear,
                label: i18next.t("TaxpayerDeclaration:Form.AppliedTo"),
                defaultValue: getUtcDate()
            },
            [PrTaxpayerDeclarationEntity.Year]: {
                label: i18next.t("TaxpayerDeclaration:Form.ForYear"),
                type: FieldType.ComboBox,
                defaultValue: getUtcDate().getFullYear(),
                fieldSettings: {
                    items: [{
                        id: getUtcDate().getFullYear(),
                        label: getUtcDate().getFullYear().toString()
                    }, {
                        id: getUtcDate().getFullYear() - 1,
                        label: (getUtcDate().getFullYear() - 1).toString()
                    }]
                },
                isVisible: isNewEntity
            },
            [createPath(PrTaxpayerDeclarationEntity.Children, PrTaxpayerDeclarationChildEntity.FirstName)]: {},
            [createPath(PrTaxpayerDeclarationEntity.Children, PrTaxpayerDeclarationChildEntity.LastName)]: {},
            [createPath(PrTaxpayerDeclarationEntity.Children, PrTaxpayerDeclarationChildEntity.IsDisabilityDiscount)]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.S,
                defaultValue: false,
                fieldSettings: {
                    items: getSimpleBoolSelectItems()
                }
            },
            [createPath(PrTaxpayerDeclarationEntity.Children, PrTaxpayerDeclarationChildEntity.Order)]: {
                type: FieldType.ComboBox,
                width: FastEntryInputSizes.XS,
                fieldSettings: {
                    displayName: "Name"
                }
            },
            [disabilityDiscountType]: {
                type: FieldType.ComboBox,
                label: i18next.t("TaxpayerDeclaration:Form.DiscountType"),
                isRequired: true,
                isVisible: args => {
                    return !!args.storage.getValueByPath(hasDisabilityDiscount);
                },
                fieldSettings: {
                    items: [{
                        id: PrTaxDiscountTypeCode.BasicDisabilityDiscount,
                        label: i18next.t("TaxpayerDeclaration:Types.Basic")
                    }, {
                        id: PrTaxDiscountTypeCode.ExtendedDisabilityDiscount,
                        label: i18next.t("TaxpayerDeclaration:Types.Extended")
                    }],
                    itemsForRender: (items: ISelectItem[]) => {
                        return items?.filter(item => isVisibleForDiscountType(item.id as PrTaxDiscountTypeCode));
                    }
                },
                defaultValue: PrTaxDiscountTypeCode.BasicDisabilityDiscount
            },
            [createPath(PrTaxpayerDeclarationEntity.Children, PrTaxpayerDeclarationChildEntity.PersonalIdentificationNumber)]: {
                ...BirthNumberFieldDef,
                width: FastEntryInputSizes.S
            },
            [isThereOtherProviderPath]: {
                type: FieldType.Switch,
                label: i18next.t("TaxpayerDeclaration:Form.IsThereOtherProvider"),
                fieldSettings: { type: SwitchType.YesNo },
                defaultValue: false,
                isVisible: args => args.storage.getValueByPath(showChildrenPath)
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.FirstName)]: {
                isVisible: isThereOtherProvider
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.LastName)]: {
                isVisible: isThereOtherProvider
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.PersonalIdentificationNumber)]: {
                ...BirthNumberFieldDef,
                isVisible: isThereOtherProvider
            },
            ...getAddressFields(createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.Address), {
                isVisible: isThereOtherProvider
            }),
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.IsApplyingTaxBenefits)]: {
                type: FieldType.Switch,
                fieldSettings: { type: SwitchType.YesNo },
                isVisible: isThereOtherProvider
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.IsEmployed)]: {
                type: FieldType.Switch,
                fieldSettings: { type: SwitchType.YesNo },
                isVisible: isThereOtherProvider
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerName)]: {
                ...SingleBusinessPartnerDef,
                isVisible: ifAll(isEmployed, isThereOtherProvider),
                fieldSettings: {
                    localDependentFields: [
                        {
                            from: { id: "Name" },
                            to: { id: PrTaxpayerDeclarationOtherProviderEntity.EmployerName },
                            navigateFrom: NavigationSource.Parent
                        },
                        {
                            from: { id: "BillingAddress/Street" },
                            to: { id: createPath(PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, AddressEntity.Street) },
                            navigateFrom: NavigationSource.Parent
                        },
                        {
                            from: { id: "BillingAddress/City" },
                            to: { id: createPath(PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, AddressEntity.City) },
                            navigateFrom: NavigationSource.Parent
                        },
                        {
                            from: { id: "BillingAddress/Country" },
                            to: { id: createPath(PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, AddressEntity.Country) },
                            navigateFrom: NavigationSource.Parent
                        },
                        {
                            from: { id: "BillingAddress/PostalCode" },
                            to: { id: createPath(PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, AddressEntity.PostalCode) },
                            navigateFrom: NavigationSource.Parent
                        }
                    ]
                }
            },
            ...getAddressFields(createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress), {
                isVisible: ifAll(isEmployed, isThereOtherProvider)
            })
        },
        groups: [
            {
                id: TaxPayerDeclarationGroupIds.Validity,
                rows: [[
                    { id: PrTaxpayerDeclarationEntity.DateApplyFrom },
                    { id: PrTaxpayerDeclarationEntity.DateApplyTo }
                ]]
            }, {
                id: TaxPayerDeclarationGroupIds.Employee,
                rows: [[{ id: PrTaxpayerDeclarationEntity.Employee }]]
            },
            getGroupForDeductionType(PrTaxDiscountTypeCode.TaxPayerDiscount),
            {
                id: PrTaxpayerDeclarationEntity.Children,
                title: i18next.t("TaxpayerDeclaration:Types.Children"),
                togglePropPath: showChildrenPath,
                isSmallBottomMargin: true,
                lineItems: {
                    additionalFields: [],
                    collection: PrTaxpayerDeclarationEntity.Children,
                    order: "Id",
                    isItemCloneable: false,
                    canAdd: args => args.storage.getValueByPath(showChildrenPath),
                    isItemVisible: args => args.storage.getValueByPath(showChildrenPath),
                    columns: [
                        { id: PrTaxpayerDeclarationChildEntity.Order },
                        { id: PrTaxpayerDeclarationChildEntity.FirstName },
                        { id: PrTaxpayerDeclarationChildEntity.LastName },
                        { id: PrTaxpayerDeclarationChildEntity.PersonalIdentificationNumber },
                        { id: PrTaxpayerDeclarationChildEntity.IsDisabilityDiscount }
                    ]
                }
            }, {
                id: PrTaxpayerDeclarationEntity.OtherProvider,
                isVisible: args => args.storage.getValueByPath(showChildrenPath),
                rows: [[{
                    id: isThereOtherProviderPath
                }], [
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.FirstName) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.LastName) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.PersonalIdentificationNumber) }
                ], [
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.Address, PrOtherProviderAddressEntity.Street) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.Address, PrOtherProviderAddressEntity.City) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.Address, PrOtherProviderAddressEntity.PostalCode) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.Address, PrOtherProviderAddressEntity.Country) },
                ], [
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.IsApplyingTaxBenefits) }
                ], [
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.IsEmployed) }
                ], [
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerName) }
                ], [
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, PrOtherProviderEmployerAddressEntity.Street) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, PrOtherProviderEmployerAddressEntity.City) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, PrOtherProviderEmployerAddressEntity.PostalCode) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, PrOtherProviderEmployerAddressEntity.Country) }
                ]]
            }, {
                id: "disability",
                isVisible: ifAny(isVisibleForDiscountType(PrTaxDiscountTypeCode.BasicDisabilityDiscount), isVisibleForDiscountType(PrTaxDiscountTypeCode.ExtendedDisabilityDiscount)),
                title: i18next.t("TaxpayerDeclaration:Form.DisabilityDiscount"),
                togglePropPath: hasDisabilityDiscount,
                isSmallBottomMargin: true,
                customizationData: {
                    isLocked: true
                },
                rows: [[{ id: disabilityDiscountType }]]
            },
            getGroupForDeductionType(PrTaxDiscountTypeCode.DiscountForZTP_PHolder)
        ]
    };

    return {
        entitySet: EntitySetName.PrTaxpayerDeclarations,
        table,
        form
    };
};

getDefinitions.translationFiles = ["TaxpayerDeclaration", "NumberRange"];

setDefByEntityType(EntityTypeName.PrTaxpayerDeclaration, getDefinitions);